import type {
  ServiceCategoriesPayload,
  ServiceCategoriesResponse,
  ServiceCategory,
  EditServiceCategoryPayload,
  ServiceDropoutResponse,
  ServicesListResponse,
  Service,
  ServiceDetailsResponse,
  Price,
  ServiceDetailShort,
  ServiceDetailShortPayload,
  EditDetailServicePayload,
  UnbindServiceDetailPayload,
  EditPricesServicePayload,
} from '@/types/services'
import axios from 'axios'
import type { DefaultDeletePayload, DeleteSuccessResponse, IPageQuery, ItemPayload } from '@/types/main'
import type { SelectItem } from '@/types/catalog'
import type { PaginatedData } from '@/types/httpResponse'

const BASE_PATH = '/dashboard/v1/chief/service'

const getServiceCategoriesCall = async (
  abortController?: AbortController,
  params?: ServiceCategoriesPayload,
): Promise<ServiceCategoriesResponse> => {
  const { data } = await axios.get(`${BASE_PATH}/categories`, {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const getServiceCategoryCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<ServiceCategory> => {
  const { data } = await axios.get(`${BASE_PATH}/categories/${params?.id}`, {
    signal: abortController?.signal,
  })
  return data.data
}

const getServiceSearchCategoriesCall = async (
  abortController?: AbortController,
  params?: { search: string, perPage: number },
): Promise<ServiceDropoutResponse> => {
  const { data } = await axios.get(`${BASE_PATH}/categories/dropout`, {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const addServiceCategoryCall = async (
  abortController?: AbortController,
  params?: FormData,
): Promise<ServiceCategory> => {
  const { data } = await axios.post(`${BASE_PATH}/categories`, params, {
    signal: abortController?.signal,
  })
  return data.data
}

const editServiceCategoryCall = async (
  abortController?: AbortController,
  params?: EditServiceCategoryPayload,
): Promise<ServiceCategory> => {
  const { data } = await axios.post(`${BASE_PATH}/categories/${params?.id}`, params?.data, {
    signal: abortController?.signal,
  })
  return data.data
}

const deleteServiceCategoryCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<DeleteSuccessResponse> => {
  const { data } = await axios.delete(`${BASE_PATH}/categories/${params?.id}`, {
    signal: abortController?.signal,
  })
  return data
}

const getServicesListCall = async (
  abortController?: AbortController,
  params?: ServiceCategoriesPayload,
): Promise<ServicesListResponse> => {
  const { data } = await axios.get(`${BASE_PATH}/services`, {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const getServiceSearchCall = async (
  abortController?: AbortController,
  params?: { search: string, perPage: number },
): Promise<ServiceDropoutResponse> => {
  const { data } = await axios.get(`${BASE_PATH}/services/dropout`, {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const getServiceItemCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<Service> => {
  const { data } = await axios.get(`${BASE_PATH}/services/${params?.id}`, {
    signal: abortController?.signal,
  })
  return data.data
}

const getServiceDetailsCall = async (
  abortController?: AbortController,
  params?: ItemPayload & { include_html: 1 | 0 },
): Promise<ServiceDetailsResponse> => {
  const { data } = await axios.get(`${BASE_PATH}/services/${params?.id}/details`, {
    signal: abortController?.signal,
    params: {
      include_html: params?.include_html,
    },
  })
  return data.data
}

const getServicePricesCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<Price[]> => {
  const { data } = await axios.get(`${BASE_PATH}/services/${params?.id}/prices`, {
    signal: abortController?.signal,
  })
  return data.data
}

const getServiceSearchDetailsCall = async (
  abortController?: AbortController,
  params?: { search: string },
): Promise<SelectItem[]> => {
  const { data } = await axios.get(`${BASE_PATH}/services/details/dropout`, {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const getServiceDetailsListCall = async (
  abortController?: AbortController,
  params?: IPageQuery,
): Promise<PaginatedData<ServiceDetailShort[]>> => {
  const { data } = await axios.get(`${BASE_PATH}/services/details`, {
    signal: abortController?.signal,
    params,
  })
  return data.data
}

const addServiceItemCall = async (
  abortController?: AbortController,
  params?: FormData,
): Promise<Service> => {
  const { data } = await axios.post(`${BASE_PATH}/services`, params, {
    signal: abortController?.signal,
  })
  return data.data
}

const editServiceItemCall = async (
  abortController?: AbortController,
  params?: EditServiceCategoryPayload,
): Promise<Service> => {
  const { data } = await axios.post(`${BASE_PATH}/services/${params?.id}`, params?.data, {
    signal: abortController?.signal,
  })
  return data.data
}

const deleteServiceItemCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<DeleteSuccessResponse> => {
  const { data } = await axios.delete(`${BASE_PATH}/services/${params?.id}`, {
    signal: abortController?.signal,
  })
  return data
}

const deleteServiceDetailItemCall = async (
  abortController?: AbortController,
  params?: ItemPayload,
): Promise<DeleteSuccessResponse> => {
  const { data } = await axios.delete(`${BASE_PATH}/services/details/${params?.id}`, {
    signal: abortController?.signal,
  })
  return data
}

const editServiceDetailItemCall = async (
  abortController?: AbortController,
  params?: EditDetailServicePayload,
): Promise<ServiceDetailShort> => {
  const { data } = await axios.put(`${BASE_PATH}/services/details/${params?.id}`, params?.data, {
    signal: abortController?.signal,
  })
  return data.data
}

const addServiceDetailItemCall = async (
  abortController?: AbortController,
  params?: ServiceDetailShortPayload,
): Promise<ServiceDetailShort> => {
  const { data } = await axios.post(`${BASE_PATH}/services/details`, params, {
    signal: abortController?.signal,
  })
  return data.data
}

const unbindServiceDetailItemCall = async (
  abortController?: AbortController,
  params?: UnbindServiceDetailPayload,
): Promise<ServiceDetailShort> => {
  const { data } = await axios.post(
    `${BASE_PATH}/services/${params?.id}/unbind-details`,
    params?.data,
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const addServicePricesCall = async (
  abortController?: AbortController,
  params?: EditPricesServicePayload,
): Promise<ServiceDetailShort> => {
  const { data } = await axios.post(`${BASE_PATH}/services/${params?.id}/prices`, params?.data, {
    signal: abortController?.signal,
  })
  return data.data
}

const editServicePricesCall = async (
  abortController?: AbortController,
  params?: EditPricesServicePayload,
): Promise<ServiceDetailShort> => {
  const { data } = await axios.put(
    `${BASE_PATH}/services/${params?.id}/prices/mussUpdate`,
    params?.data,
    {
      signal: abortController?.signal,
    },
  )
  return data.data
}

const deleteServicePriceCall = async (
  abortController?: AbortController,
  params?: DefaultDeletePayload,
): Promise<DeleteSuccessResponse> => {
  const { data } = await axios.delete(
    `${BASE_PATH}/services/${params?.id}/prices/${params?.uuid}`,
    {
      signal: abortController?.signal,
    },
  )
  return data
}

export {
  addServiceCategoryCall,
  addServiceDetailItemCall,
  addServiceItemCall,
  addServicePricesCall,
  deleteServiceCategoryCall,
  deleteServiceDetailItemCall,
  deleteServiceItemCall,
  deleteServicePriceCall,
  editServiceCategoryCall,
  editServiceDetailItemCall,
  editServiceItemCall,
  editServicePricesCall,
  getServiceCategoriesCall,
  getServiceCategoryCall,
  getServiceDetailsCall,
  getServiceDetailsListCall,
  getServiceItemCall,
  getServicePricesCall,
  getServiceSearchCall,
  getServiceSearchCategoriesCall,
  getServiceSearchDetailsCall,
  getServicesListCall,
  unbindServiceDetailItemCall,
}
